import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { toast } from 'sonner';
import { Lock, AlertCircle } from 'lucide-react';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ChangePassword() {
  const { user, token, logout } = useAuth();
  const navigate = useNavigate();
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);

  const handleChangePassword = async (e) => {
    e.preventDefault();

    if (newPassword !== confirmPassword) {
      toast.error('New passwords do not match');
      return;
    }

    if (newPassword.length < 6) {
      toast.error('New password must be at least 6 characters');
      return;
    }

    if (newPassword === currentPassword) {
      toast.error('New password must be different from current password');
      return;
    }

    setLoading(true);

    try {
      await axios.post(
        `${API_URL}/auth/change-password`,
        {
          current_password: currentPassword,
          new_password: newPassword
        },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      toast.success('Password changed successfully!');
      
      // Redirect to dashboard after success
      setTimeout(() => {
        window.location.reload(); // Reload to get updated user info
      }, 1500);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to change password');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 to-white flex items-center justify-center p-4">
      <Card className="w-full max-w-md shadow-lg" data-testid="change-password-card">
        <CardHeader className="text-center space-y-3">
          <div className="mx-auto w-16 h-16 bg-amber-500 rounded-2xl flex items-center justify-center shadow-md">
            <Lock className="w-8 h-8 text-white" />
          </div>
          <CardTitle className="text-3xl font-heading" data-testid="page-title">
            Password Change Required
          </CardTitle>
          <CardDescription data-testid="page-description">
            {user?.force_password_change ? (
              <div className="flex items-center gap-2 text-amber-600 bg-amber-50 p-3 rounded-lg">
                <AlertCircle className="w-5 h-5 flex-shrink-0" />
                <span className="text-sm text-left">
                  You are using a temporary password. Please change it to a secure password before continuing.
                </span>
              </div>
            ) : (
              'Update your password to keep your account secure'
            )}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleChangePassword} className="space-y-4" data-testid="change-password-form">
            <div className="space-y-2">
              <Label htmlFor="current_password">Current Password</Label>
              <Input
                id="current_password"
                type="password"
                placeholder="Enter current password"
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                required
                className="h-12"
                data-testid="current-password-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="new_password">New Password</Label>
              <Input
                id="new_password"
                type="password"
                placeholder="Enter new password (min 6 characters)"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                required
                minLength={6}
                className="h-12"
                data-testid="new-password-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="confirm_password">Confirm New Password</Label>
              <Input
                id="confirm_password"
                type="password"
                placeholder="Re-enter new password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
                minLength={6}
                className="h-12"
                data-testid="confirm-password-input"
              />
            </div>
            <Button
              type="submit"
              className="w-full h-12 rounded-full text-base font-medium"
              disabled={loading}
              data-testid="change-password-button"
            >
              {loading ? 'Changing Password...' : 'Change Password'}
            </Button>
          </form>

          {!user?.force_password_change && (
            <Button
              variant="outline"
              className="w-full mt-4"
              onClick={handleLogout}
              data-testid="logout-button"
            >
              Cancel & Logout
            </Button>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
